import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconTrash } from '../components/icons';
import { Empty, Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { api, AttributeDef, AttributeType, ConsentRecord, Contact, ContactEvent, Paged } from '../lib/api';
import { ago, dateTime } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { ConsentPill, errorText, isAdmin, TYPE_LABEL } from './Contacts';
import { Enrollment } from '../journeys/model';
import { EnrollmentPill } from './journeys/JourneyRunLog';
import { ContactChannels } from '../channels/ContactChannels';
import { t } from '../lib/i18n';

const SUPPRESSION_REASON: Record<string, string> = {
  hard_bounce: 'жёсткий отскок',
  complaint: 'жалоба на спам',
  unsubscribe: 'отписка',
  manual: 'вручную',
  mailbox_full_repeated: 'ящик переполнен 3 раза за 30 дней',
};

/** Turns the text of an input into a value of the declared type (lists: comma-separated). */
function parseValue(raw: string, type: AttributeType | undefined): unknown {
  const v = raw.trim();
  switch (type) {
    case 'number':
      return Number(v.replace(',', '.'));
    case 'boolean':
      return /^(true|да|1)$/i.test(v);
    case 'string_list':
      return v.split(',').map((s) => s.trim()).filter(Boolean);
    default:
      return v;
  }
}

const show = (v: unknown) => (Array.isArray(v) ? v.join(', ') : typeof v === 'boolean' ? (v ? t('да') : t('нет')) : String(v));

export function ContactDetailPage() {
  const { id = '' } = useParams();
  const { me } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const contact = useAsync(() => api.get<Contact>(`/contacts/${id}`), [id]);
  const schema = useAsync(() => api.get<{ items: AttributeDef[] }>('/contacts/attributes'));
  const consents = useAsync(() => api.get<ConsentRecord[]>(`/contacts/${id}/consents`), [id]);
  const events = useAsync(() => api.get<Paged<ContactEvent>>(`/contacts/${id}/events?limit=50`), [id]);
  const journeys = useAsync(() => api.get<{ items: Enrollment[] }>(`/contacts/${id}/journeys`), [id]);
  const [attrKey, setAttrKey] = useState('');
  const [attrValue, setAttrValue] = useState('');
  const [consentOpen, setConsentOpen] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const writer = canWrite(me) && !me?.support;
  const admin = isAdmin(me?.user.role) && !me?.support;
  const types = new Map((schema.data?.items ?? []).map((a) => [a.key, a.type]));

  if (contact.loading && !contact.data) return <div className="stack"><Skeleton h={32} w={360} /><Skeleton h={240} /></div>;
  const c = contact.data;
  if (!c) return <Empty title={t('Контакт не найден')}>{t('Возможно, он удалён по запросу субъекта данных.')}</Empty>;

  async function setAttribute(e: FormEvent) {
    e.preventDefault();
    try {
      await api.patch(`/contacts/${id}`, { attributes: { [attrKey.trim()]: parseValue(attrValue, types.get(attrKey.trim())) } });
      setAttrKey('');
      setAttrValue('');
      contact.reload();
      schema.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }
  async function removeAttribute(key: string) {
    if (!confirm(t('Удалить атрибут {key}?', { key }))) return;
    try {
      await api.patch(`/contacts/${id}`, { attributes: { [key]: null } });
      contact.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }

  const suppressed = (c.suppressions ?? []).length > 0;
  return (
    <>
      <Link to="/contacts" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}><IconArrowLeft size={15} /> {t('Все контакты')}</Link>
      <div className="page-head">
        <div className="grow" style={{ minWidth: 0 }}>
          <h1 style={{ overflowWrap: 'anywhere' }}>{c.email ?? c.external_id}</h1>
          <p className="row wrap" style={{ gap: 8 }}>
            <ConsentPill consent={c.consent} />
            {c.external_id && <span className="small">external_id <code>{c.external_id}</code></span>}
            <span className="small muted"><code>{c.id}</code></span>
          </p>
        </div>
        {admin && <button className="btn btn-danger" onClick={() => setEraseOpen(true)}><IconTrash size={15} /> {t('Удалить по GDPR')}</button>}
      </div>

      {suppressed && (
        <div className="callout bad" style={{ marginBottom: 16 }}>
          <span>
            {t('Адрес в подавлениях: {list}.', { list: (c.suppressions ?? []).map((s) => t('{scope} ({reason})', { scope: t(s.scope === 'all' ? 'все письма' : 'маркетинг'), reason: t(SUPPRESSION_REASON[s.reason] ?? s.reason) })).join(', ') })}
            {t('Подавление сильнее согласия: новое «согласен» его не снимает. Снять может только человек — в разделе')} <Link to="/suppressions">{t('Подавления')}</Link>.
          </span>
        </div>
      )}

      <div className="grid grid-2">
        <div className="stack" style={{ minWidth: 0 }}>
          <div className="card">
            <div className="card-head"><h3>{t('Атрибуты')}</h3></div>
            {!Object.keys(c.attributes).length ? (
              <div className="card-pad small muted">{t('Атрибутов нет.')}</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>{t('Ключ')}</th><th>{t('Тип')}</th><th>{t('Значение')}</th>{writer && <th />}</tr></thead>
                  <tbody>
                    {Object.entries(c.attributes).map(([k, v]) => (
                      <tr key={k}>
                        <td><code>{k}</code></td>
                        <td className="small muted">{types.get(k) ? t(TYPE_LABEL[types.get(k)!]) : '—'}</td>
                        <td style={{ overflowWrap: 'anywhere' }}>{show(v)}</td>
                        {writer && <td style={{ textAlign: 'right' }}><button className="btn btn-sm btn-ghost" onClick={() => removeAttribute(k)} aria-label={t('Удалить {key}', { key: k })}><IconTrash size={14} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {writer && (
              <form className="card-pad row wrap" style={{ borderTop: '1px solid var(--border)' }} onSubmit={setAttribute}>
                <input className="input input-mono" style={{ flex: 1, minWidth: 120 }} list="attr-keys" placeholder={t('ключ')} value={attrKey} onChange={(e) => setAttrKey(e.target.value)} aria-label={t('Ключ атрибута')} />
                <datalist id="attr-keys">{schema.data?.items.map((a) => <option key={a.key} value={a.key}>{t(TYPE_LABEL[a.type])}</option>)}</datalist>
                <input className="input" style={{ flex: 2, minWidth: 140 }} placeholder={types.get(attrKey.trim()) ? t(TYPE_LABEL[types.get(attrKey.trim())!]) : t('значение')} value={attrValue} onChange={(e) => setAttrValue(e.target.value)} aria-label={t('Значение')} />
                <button className="btn" disabled={!attrKey.trim()}>{t('Сохранить')}</button>
              </form>
            )}
          </div>

          <div className="card">
            <div className="card-head"><h3>{t('События')}</h3><span className="small muted">{t('последние 50')}</span></div>
            {events.loading ? (
              <div className="card-pad"><Skeleton /></div>
            ) : !events.data?.items.length ? (
              <div className="card-pad small muted">{t('Событий нет. Их передают через')} <code>POST /v1/events</code>.</div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {events.data.items.map((e) => (
                  <details key={e.id} className="card-pad" style={{ borderTop: '1px solid var(--border)' }}>
                    <summary className="row wrap" style={{ cursor: 'pointer', gap: 8 }}>
                      <code>{e.name}</code>
                      <span className="small muted" title={dateTime(e.occurred_at)}>{ago(e.occurred_at)}</span>
                      {e.source === 'system' && <span className="pill cancelled">{t('система')}</span>}
                    </summary>
                    <pre className="code" style={{ marginTop: 8 }}>{JSON.stringify(e.properties, null, 2)}</pre>
                    {e.idempotency_key && <div className="small muted">idempotency_key <code>{e.idempotency_key}</code></div>}
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="stack" style={{ minWidth: 0 }}>
          <div className="card">
            <div className="card-head">
              <h3>{t('Согласие на маркетинг')}</h3>
              {admin && <button className="btn btn-sm" onClick={() => setConsentOpen(true)}>{t('Записать')}</button>}
            </div>
            <div className="card-pad stack">
              <div className="small muted">{t('Транзакционные письма согласия не требуют. Нет записи — маркетинг не отправляется. Действует самая поздняя по времени запись.')}</div>
              {!consents.data?.length ? (
                <div className="small">{t('Записей нет.')}</div>
              ) : (
                consents.data.map((r) => (
                  <div key={r.id} className="stack" style={{ gap: 4, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                    <div className="row wrap" style={{ gap: 8 }}>
                      <span className={`pill ${r.status === 'granted' ? 'ok' : 'bad'}`}>{r.status === 'granted' ? t('согласие') : t('отзыв')}</span>
                      <code>{r.source}</code>
                      <span className="small muted" title={t('записано {date}', { date: dateTime(r.recorded_at) })}>{dateTime(r.occurred_at)}</span>
                    </div>
                    {Object.keys(r.proof).length > 0 && <pre className="code" style={{ margin: 0 }}>{JSON.stringify(r.proof, null, 2)}</pre>}
                    <div className="small muted">{t('записал: {who}', { who: r.recorded_by })}</div>
                  </div>
                ))
              )}
            </div>
          </div>
          <ContactChannels contactId={id} writer={writer} />
          <div className="card">
            <div className="card-head"><h3>{t('Сценарии')}</h3><span className="small muted">{t('участия контакта')}</span></div>
            {!journeys.data?.items.length ? (
              <div className="card-pad small muted">{journeys.loading ? t('Загружаю…') : t('Контакт не участвовал в сценариях.')}</div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {journeys.data.items.map((e) => (
                  <Link key={e.id} to={`/journeys/${e.journey_id}?tab=enrollments&e=${e.id}`} className="card-pad row wrap" style={{ gap: 8, borderTop: '1px solid var(--border)', color: 'inherit', textDecoration: 'none' }}>
                    <span style={{ flex: 1, minWidth: 0 }}><b>{e.journey?.name ?? e.journey_id}</b><div className="small muted">{t('v{v} · {where} · {when}', { v: e.version, where: e.group === 'control' ? t('контрольная группа') : e.current_step ? t('шаг {n}', { n: e.current_step }) : t('вход'), when: ago(e.entered_at) })}</div></span>
                    <EnrollmentPill e={e} />
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div className="card">
            <div className="card-head"><h3>{t('Профиль')}</h3></div>
            <div className="card-pad stack small">
              <div>{t('Часовой пояс:')} <strong>{c.timezone ?? t('пояс пространства')}</strong></div>
              <div>{t('Локаль:')} <strong>{c.locale ?? '—'}</strong></div>
              <div>{t('Добавлен:')} {dateTime(c.created_at)}</div>
              <div>{t('Изменён:')} {dateTime(c.updated_at)}</div>
              <div>{t('Последнее событие:')} {c.last_event_at ? dateTime(c.last_event_at) : '—'}</div>
            </div>
          </div>
        </div>
      </div>

      {consentOpen && <ConsentModal id={id} onClose={() => setConsentOpen(false)} onSaved={() => (setConsentOpen(false), contact.reload(), consents.reload())} />}
      {eraseOpen && <EraseModal contact={c} onClose={() => setEraseOpen(false)} onErased={() => nav('/contacts')} />}
    </>
  );
}

function ConsentModal({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [status, setStatus] = useState<'granted' | 'withdrawn'>('granted');
  const [source, setSource] = useState('dashboard');
  const [text, setText] = useState('');
  async function save() {
    try {
      const r = await api.post<{ warnings?: { message: string }[] }>(`/contacts/${id}/consent`, { status, source, proof: { text } });
      toast(r.warnings?.[0]?.message ?? t('Записано'), r.warnings ? 'bad' : 'ok');
      onSaved();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }
  return (
    <Modal title={t('Запись о согласии')} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-primary" onClick={save} disabled={text.trim().length < 3 || !source.trim()}>{t('Записать')}</button></>}>
      <div className="chips">
        <button className={`chip${status === 'granted' ? ' on' : ''}`} onClick={() => setStatus('granted')}>{t('Согласие')}</button>
        <button className={`chip${status === 'withdrawn' ? ' on' : ''}`} onClick={() => setStatus('withdrawn')}>{t('Отзыв')}</button>
      </div>
      <Field label={t('Источник')}><input className="input input-mono" value={source} onChange={(e) => setSource(e.target.value)} maxLength={64} /></Field>
      <Field label={t('Как получено')} hint={t('Обязательно: это доказательство на случай проверки')}>
        <textarea className="input" rows={3} placeholder={t('Устно по телефону 11.10, звонок №1234')} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
    </Modal>
  );
}

function EraseModal({ contact, onClose, onErased }: { contact: Contact; onClose: () => void; onErased: () => void }) {
  const toast = useToast();
  const [suppress, setSuppress] = useState(true);
  const [typed, setTyped] = useState('');
  const confirmWord = contact.email ?? contact.external_id ?? '';
  async function erase() {
    try {
      await api.del(`/contacts/${contact.id}${suppress ? '?suppress=true' : ''}`);
      toast(t('Контакт удалён. Квитанция без персональных данных сохранена.'));
      onErased();
    } catch (err) {
      toast(errorText(err), 'bad');
    }
  }
  return (
    <Modal title={t('Удалить по GDPR')} onClose={onClose} footer={<><button className="btn" onClick={onClose}>{t('Отмена')}</button><button className="btn btn-danger" onClick={erase} disabled={typed.trim() !== confirmWord}>{t('Удалить навсегда')}</button></>}>
      <p>{t('Удалятся контакт, его атрибуты, события, согласия и членство в сегментах. Отменить нельзя.')}</p>
      <p className="small muted">{t('Подавления адреса остаются — это минимальное доказательство отказа, чтобы повторный импорт не вернул адрес в рассылки. Журнал доставки писем живёт по своему сроку хранения.')}</p>
      <label className="row" style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={suppress} onChange={(e) => setSuppress(e.target.checked)} /> {t('Также подавить адрес для маркетинга')}
      </label>
      <Field label={t('Введите {what} для подтверждения', { what: contact.email ? t('адрес') : 'external_id' })}>
        <input className="input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={confirmWord} />
      </Field>
    </Modal>
  );
}
